import R from 'ramda';
import { createReducer } from 'ha/myredux/createReducer';
import { PAYMENT_REQUEST_CREATED } from 'ha/modules/PaymentRequests/actions';

import {
  addFiles,
  removeFile,
  setMessage,
  sendMessageAction,
  sendFileAction,
  setMakingSpecialOffer,
  unsetMakingSpecialOffer,
  makeRequestAction,
  setRequestingAnotherDates,
  unsetRequestingAnotherDates,
  markAsReadAction,
  setDeclining,
  unsetDeclining,
  addMessage,
  readMessages,
  resetState,
  updateConversation,
  load,
} from '../actions';

const emptyChatForm = {
  message: '',
  files: [],
  submitting: false,
  error: null,
};

export const initialState = {
  initial: true,
  chatForm: {
    message: '',
    files: [],
    submitting: false,
    error: null,
  },
  declining: false,
  endDate: '',
  error: null,
  isLoading: false,
  makingSpecialOffer: false,
  messages: [],
  payoutInformation: null,
  requesting: null,
  requestingAnotherDates: false,
  stateHistory: [],
  tenantMode: false,
  currency: '',
  id: null,
  listingId: null,
  tenantId: null,
  tenant: null,
  advertiserId: null,
  advertiser: null,
  createdAt: '',
  stateChangedAt: '',
  startDate: '',
  price: null,
  deposit: null,
  fee: null,
  baseFee: null,
  total: null,
  state: null,
  stateLabel: '',
  declinedBy: null,
  messageCount: 0,
  paymentMethodId: null,
  flagUnread: false,
  flagArchived: false,
  flagStarred: false,
  tenantsCount: null,
  listing: {
    agencyFee: null,
    alias: null,
    city: null,
    country: null,
    endDate: null,
    exclusions: [],
    id: null,
    minimumStayMonths: null,
    listingPath: null,
    startDate: null,
    street: null,
    previewImage: null,
    isDeleted: false,
  },
  paymentRequestCreated: false,
};

export default createReducer(
  {
    [resetState]: () => initialState,
    [addFiles]: (atom, files) => ({
      ...atom,
      chatForm: {
        ...atom.chatForm,
        files: [...atom.chatForm.files, ...files],
      },
    }),
    [removeFile]: (atom, fileIndex) => ({
      ...atom,
      chatForm: {
        ...atom.chatForm,
        files: atom.chatForm.files.filter((file, index) => index !== fileIndex),
      },
    }),
    [setMessage]: (atom, message) => ({
      ...atom,
      chatForm: {
        ...atom.chatForm,
        message,
      },
    }),
    [sendMessageAction.start]: atom => ({
      ...atom,
      chatForm: {
        ...atom.chatForm,
        submitting: true,
        error: null,
      },
    }),
    [sendMessageAction.done]: (atom, messages) => ({
      ...atom,
      messages,
      chatForm: emptyChatForm,
    }),
    [sendMessageAction.error]: atom => ({
      ...atom,
      chatForm: {
        ...atom.chatForm,
        submitting: false,
      },
    }),
    [sendFileAction.start]: atom => ({
      ...atom,
      chatForm: {
        ...atom.chatForm,
        submitting: true,
        error: null,
      },
    }),
    [sendFileAction.done]: (atom, newMessage) => ({
      ...atom,
      messages: [newMessage, ...atom.messages],
      chatForm: { ...emptyChatForm, message: atom.chatForm.message },
    }),
    [sendFileAction.error]: atom => ({
      ...atom,
      chatForm: {
        ...atom.chatForm,
        submitting: false,
      },
    }),
    [setMakingSpecialOffer]: atom => ({
      ...atom,
      makingSpecialOffer: true,
    }),
    [unsetMakingSpecialOffer]: atom => ({
      ...atom,
      makingSpecialOffer: false,
    }),
    [setRequestingAnotherDates]: atom => ({
      ...atom,
      requestingAnotherDates: true,
    }),
    [unsetRequestingAnotherDates]: atom => ({
      ...atom,
      requestingAnotherDates: false,
    }),
    [setDeclining]: atom => ({
      ...atom,
      declining: true,
    }),
    [unsetDeclining]: atom => ({
      ...atom,
      declining: false,
    }),
    [makeRequestAction.start]: (atom, { type }) => ({
      ...atom,
      requesting: type,
      requestError: null,
    }),
    [makeRequestAction.done]: (atom, payload) => ({
      ...atom,
      ...payload,
      requesting: null,
      declining: false,
      makingSpecialOffer: false,
      requestingAnotherDates: false,
    }),
    [makeRequestAction.error]: atom => ({
      ...atom,
      requesting: null,
    }),
    [markAsReadAction.done]: atom => ({
      ...atom,
    }),
    [load.start]: atom => ({
      ...initialState,
      ...atom,
      isLoading: true,
    }),
    [load.error]: (atom, payload) => ({
      ...atom,
      isLoading: false,
      error: payload,
    }),
    [load.done]: (atom, payload) => ({
      ...atom,
      ...payload,
      initial: false,
      isLoading: false,
    }),
    [addMessage]: (atom, newMessage) =>
      atom.id === newMessage.conversationId
        ? {
            ...atom,
            messages: [newMessage, ...atom.messages],
            messageCount: atom.messageCount + 1,
          }
        : atom,
    [readMessages]: (atom, eventObj) =>
      atom.id === eventObj.conversationId
        ? {
            ...atom,
            messages: atom.messages.map(msg => ({ ...msg, isRead: true })),
          }
        : atom,
    [updateConversation.done]: (atom, payload) => ({
      ...atom,
      ...payload,
    }),
    [PAYMENT_REQUEST_CREATED]: atom => ({
      ...atom,
      paymentRequestCreated: true,
    }),
  },
  initialState,
);

export const getInitial = R.prop(['initial']);
export const getAcceptedBy = R.prop(['acceptedBy']);
export const getConversationId = R.prop(['id']);
export const getConversationState = R.prop(['state']);
export const getCurrency = R.prop(['currency']);
export const getDeclinedBy = R.prop(['declinedBy']);
export const getDeposit = R.prop(['deposit']);
export const getDepositWithCurrency = R.prop(['depositWithCurrency']);
export const getEndDate = R.prop(['endDate']);
export const getFee = R.prop(['fee']);
export const getPrice = R.prop(['price']);
export const getStartDate = R.prop(['startDate']);
export const getStateChangedAt = R.prop(['stateChangedAt']);
export const getStateHistory = R.prop(['stateHistory']);
export const getTenantId = R.prop(['tenantId']);
export const getAdvertiserId = R.prop(['advertiserId']);
export const getTotal = R.prop(['total']);
export const getTenant = R.prop(['tenant']);
export const getAdvertiser = R.prop(['advertiser']);
export const getMessages = R.prop(['messages']);
export const getError = R.prop(['error']);
export const paymentRequestCreated = R.prop(['paymentRequestCreated']);

export const getListing = R.prop('listing');
export const getAgencyFee = R.pipe(
  getListing,
  R.prop('agencyFee'),
);
export const getAlias = R.pipe(
  getListing,
  R.prop('alias'),
);
export const getCity = R.pipe(
  getListing,
  R.prop('city'),
);
export const getCountry = R.pipe(
  getListing,
  R.prop('country'),
);
export const getExclusions = R.pipe(
  getListing,
  R.prop('exclusions'),
);
export const getListingId = R.pipe(
  getListing,
  R.prop('id'),
);
export const getMinimumStayMonths = R.pipe(
  getListing,
  R.prop('minimumStayMonths'),
);
export const getListingPath = R.pipe(
  getListing,
  R.prop('listingPath'),
);
export const getStreet = R.pipe(
  getListing,
  R.prop('street'),
);
export const getPhotoUrlList = R.pipe(
  getListing,
  R.prop('photoURLList'),
);
export const getIsListingDeleted = R.pipe(
  getListing,
  R.prop('isDeleted'),
);
